import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Tooltip, Skeleton, CircularProgress } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Print,
  CheckCircleOutline,
  WarningAmberOutlined,
  HelpOutline,
  AccessTime,
  Person,
  LocationOn,
  VerifiedOutlined,
  Group,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import {
  useGetBranchTallyDetailQuery,
  useVerifyBranchTallyMutation,
} from "store/services/branchTallyApi";
import { useAuth } from "hooks";

const initials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const fmt = (d) =>
  d
    ? new Date(d).toLocaleString("en-NP", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const UserAvatar = ({ name, size = 30 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: "linear-gradient(135deg,#1e3a5f,#2563eb)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size * 0.33,
      fontWeight: 800,
      flexShrink: 0,
      letterSpacing: 0.5,
    }}
  >
    {initials(name)}
  </div>
);

const StatusBadge = ({ status }) => {
  const theme = useTheme();
  const isCompleted = status === "Completed";
  const color = isCompleted
    ? theme.palette.success.main
    : theme.palette.warning.main;
  return (
    <span
      style={{
        background: alpha(color, 0.15),
        color,
        padding: "3px 12px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.4,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      {isCompleted ? "Completed" : "In Progress"}
    </span>
  );
};

const InfoRow = ({ icon, label, value }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 0",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <span
        style={{
          color: theme.palette.text.secondary,
          marginTop: 1,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: 0.6,
          }}
        >
          {label}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {value || "—"}
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, color, bg, icon: Icon }) => (
  <div
    style={{
      flex: 1,
      minWidth: 120,
      background: bg,
      borderRadius: 12,
      padding: "16px 20px",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color,
            textTransform: "uppercase",
            letterSpacing: 0.7,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 30,
            fontWeight: 900,
            color,
            fontFamily: "monospace",
            lineHeight: 1.2,
            marginTop: 4,
          }}
        >
          {value ?? "—"}
        </div>
      </div>
      {Icon && <Icon sx={{ fontSize: 32, color, opacity: 0.2 }} />}
    </div>
  </div>
);

const ShipmentChip = ({ id, color }) => (
  <span
    style={{
      display: "inline-block",
      fontFamily: "monospace",
      fontWeight: 700,
      fontSize: 11,
      color,
      background: alpha(color, 0.08),
      border: `1px solid ${alpha(color, 0.2)}`,
      borderRadius: 6,
      padding: "2px 8px",
      letterSpacing: 0.3,
      lineHeight: 1.8,
    }}
  >
    {id}
  </span>
);

const ParticipantsList = ({ participants = [] }) => {
  if (participants.length === 0) {
    return (
      <span style={{ color: "#94a3b8", fontSize: 13 }}>No participants</span>
    );
  }
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}
    >
      {participants.map((p) => (
        <div
          key={p._id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: p.isCreator ? "#eff6ff" : "#f8fafc",
            border: `1px solid ${p.isCreator ? "#bfdbfe" : "#e2e8f0"}`,
            borderRadius: 10,
            padding: "7px 12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <UserAvatar name={p.name} size={26} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                {p.name}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                {p.completedAt
                  ? `Completed ${fmt(p.completedAt)}`
                  : `Joined ${fmt(p.joinedAt)}`}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {p.isCreator && (
              <span
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  borderRadius: 20,
                  padding: "2px 9px",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Creator
              </span>
            )}
            {p.completedAt && (
              <span
                style={{
                  background: "#059669",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  borderRadius: 20,
                  padding: "2px 9px",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Done
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const ScannerGroupCard = ({ scannerName, shipments, color, emptyMsg }) => {
  const [expanded, setExpanded] = useState(true);
  const count = shipments.length;
  return (
    <div
      style={{
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 10,
      }}
    >
      <div
        onClick={() => setExpanded((e) => !e)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          background: alpha(color, 0.06),
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {scannerName && <UserAvatar name={scannerName} size={28} />}
          <div>
            <div style={{ fontWeight: 800, fontSize: 13, color: "#0f172a" }}>
              {scannerName || "Unknown"}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
              {count} shipment{count !== 1 ? "s" : ""} scanned
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              background: color,
              color: "#fff",
              borderRadius: 20,
              padding: "2px 12px",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "monospace",
            }}
          >
            {count}
          </span>
          <span
            style={{
              color: "#94a3b8",
              fontSize: 16,
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
            }}
          >
            ▾
          </span>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: "12px 16px", background: "#fff" }}>
          {count === 0 ? (
            <div
              style={{
                color: "#94a3b8",
                fontSize: 13,
                textAlign: "center",
                padding: "12px 0",
              }}
            >
              {emptyMsg}
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {shipments.map((id, i) => (
                <ShipmentChip key={i} id={id} color={color} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GroupedTalliedContent = ({ rows = [] }) => {
  const groups = rows.reduce((acc, r) => {
    const key = r.scannedBy || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(r.shipmentId);
    return acc;
  }, {});
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 13,
        }}
      >
        No tallied shipments yet.
      </div>
    );
  }
  return (
    <div style={{ padding: 16 }}>
      {Object.entries(groups).map(([name, shipments]) => (
        <ScannerGroupCard
          key={name}
          scannerName={name}
          shipments={shipments}
          color="#059669"
          emptyMsg="No shipments"
        />
      ))}
    </div>
  );
};

const GroupedMissingPhysicalContent = ({ rows = [] }) => {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 13,
        }}
      >
        🎉 All expected shipments found physically.
      </div>
    );
  }
  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          border: "1px solid #fee2e2",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            background: "#fff5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 13, color: "#dc2626" }}>
            Not found physically
          </div>
          <span
            style={{
              background: "#dc2626",
              color: "#fff",
              borderRadius: 20,
              padding: "2px 12px",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "monospace",
            }}
          >
            {rows.length}
          </span>
        </div>
        <div
          style={{
            padding: "12px 16px",
            background: "#fff",
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {rows.map((r, i) => (
            <ShipmentChip key={i} id={r.shipmentId || r} color="#dc2626" />
          ))}
        </div>
      </div>
    </div>
  );
};

const GroupedMissingSystemContent = ({ rows = [] }) => {
  const groups = rows.reduce((acc, r) => {
    const key = r.scannedBy || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(r.scannedShipmentId);
    return acc;
  }, {});
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 13,
        }}
      >
        🎉 No unrecognised shipments scanned.
      </div>
    );
  }
  return (
    <div style={{ padding: 16 }}>
      {Object.entries(groups).map(([name, shipments]) => (
        <ScannerGroupCard
          key={name}
          scannerName={name}
          shipments={shipments}
          color="#9333ea"
          emptyMsg="No shipments"
        />
      ))}
    </div>
  );
};

const TABS = [
  {
    id: "tallied",
    label: "Tallied",
    icon: <CheckCircleOutline fontSize="small" />,
    color: "#059669",
    countKey: "talliedCount",
  },
  {
    id: "missingPhysical",
    label: "Missing Physical",
    icon: <WarningAmberOutlined fontSize="small" />,
    color: "#dc2626",
    countKey: "missingPhysicalCount",
  },
  {
    id: "missingSystem",
    label: "Missing System",
    icon: <HelpOutline fontSize="small" />,
    color: "#9333ea",
    countKey: "missingSystemCount",
  },
];

const DetailSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    <Skeleton variant="rounded" height={110} />
    <div style={{ display: "flex", gap: 12 }}>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={90} style={{ flex: 1 }} />
      ))}
    </div>
    <Skeleton variant="rounded" height={300} />
  </div>
);

const BranchTallyDetailScreen = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("tallied");
  const theme = useTheme();
  const { loggedInUser } = useAuth();
  const { data: tally, isLoading, isError } = useGetBranchTallyDetailQuery(id, {
  refetchOnMountOrArgChange: true,
});
  const [verifyTally, { isLoading: verifying }] =
    useVerifyBranchTallyMutation();

  const breadcrumbs = [
    { title: "Branch Tally", path: "/app/crm/branch-tally/list" },
    { title: "Detail" },
  ];

  const handleVerify = async () => {
    try {
      await verifyTally(id).unwrap();
      toast.success("Tally verified successfully!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't verify tally.");
    }
  };

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={
        <div style={{ display: "flex", gap: 8 }}>
          {tally?.status === "Completed" && (
            <Link
              to={`/app/crm/branch-tally/print/${id}`}
              state={{ record: tally }}
            >
              <Button startIcon={<Print />} variant="contained" size="small">
                Print Report
              </Button>
            </Link>
          )}
          {tally?.status === "Completed" &&
            !tally?.approvedBy &&
            tally?.initiatedBy?._id?.toString() ===
              loggedInUser?._id?.toString() && (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={
                  verifying ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <VerifiedOutlined />
                  )
                }
                disabled={verifying}
                onClick={handleVerify}
                sx={{ fontWeight: 700 }}
              >
                {verifying ? "Verifying…" : "Verify"}
              </Button>
            )}
          {tally?.status === "Completed" && tally?.approvedBy && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#d1fae5",
                border: "1px solid #6ee7b7",
                borderRadius: 8,
                padding: "5px 12px",
                fontSize: 12,
                fontWeight: 700,
                color: "#065f46",
              }}
            >
              <VerifiedOutlined style={{ fontSize: 16 }} />
              Verified by {tally.approvedBy.name}
            </div>
          )}
        </div>
      }
    >
      {isLoading ? (
        <BreadcrumbLayout.Paper>
          <DetailSkeleton />
        </BreadcrumbLayout.Paper>
      ) : isError || !tally ? (
        <BreadcrumbLayout.Paper>
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            Failed to load tally detail.
          </div>
        </BreadcrumbLayout.Paper>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                background: theme.palette.background.paper,
                borderRadius: 14,
                border: `1px solid ${theme.palette.divider}`,
                padding: "18px 22px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#0f172a",
                  }}
                >
                  Session Info
                </h3>
                <StatusBadge status={tally.status} />
              </div>
              <InfoRow
                icon={<LocationOn fontSize="small" />}
                label="Branch"
                value={tally.branch?.name}
              />
              <InfoRow
                icon={<AccessTime fontSize="small" />}
                label="Started At"
                value={fmt(tally.startedAt)}
              />
              <InfoRow
                icon={<LocationOn fontSize="small" />}
                label="Branch"
                value={tally.branch?.name}
              />
              <InfoRow
                icon={<Person fontSize="small" />}
                label="Initiated By"
                value={
                  tally.initiatedBy?.name ? (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 7 }}
                    >
                      <UserAvatar name={tally.initiatedBy.name} size={24} />
                      {tally.initiatedBy.name}
                    </div>
                  ) : null
                }
              />
              <InfoRow
                icon={<AccessTime fontSize="small" />}
                label="Started At"
                value={fmt(tally.startedAt)}
              />
              {tally.completedAt && (
                <InfoRow
                  icon={<AccessTime fontSize="small" />}
                  label="Completed At"
                  value={fmt(tally.completedAt)}
                />
              )}
              <InfoRow
                icon={<AccessTime fontSize="small" />}
                label="Duration"
                value={
                  <span
                    style={{
                      fontFamily: "monospace",
                      background: "#f1f5f9",
                      padding: "1px 8px",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    {tally.duration}
                  </span>
                }
              />
            </div>
            <div
              style={{
                background: theme.palette.background.paper,
                borderRadius: 14,
                border: `1px solid ${theme.palette.divider}`,
                padding: "18px 22px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px",
                  fontSize: 14,
                  fontWeight: 800,
                  color: "#0f172a",
                }}
              >
                People
              </h3>
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Group sx={{ fontSize: 14 }} />
                  Participants ({tally.participants?.length ?? 0})
                </div>
                <ParticipantsList participants={tally.participants ?? []} />
              </div>
              {tally.approvedBy?.name && (
                <InfoRow
                  icon={<Person fontSize="small" />}
                  label="Approved By"
                  value={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 7 }}
                    >
                      <UserAvatar name={tally.approvedBy.name} size={24} />
                      {tally.approvedBy.name}
                    </div>
                  }
                />
              )}
              {tally.signedBy?.name && (
                <InfoRow
                  icon={<Person fontSize="small" />}
                  label="Signed By"
                  value={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 7 }}
                    >
                      <UserAvatar name={tally.signedBy.name} size={24} />
                      {tally.signedBy.name}
                    </div>
                  }
                />
              )}
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    marginBottom: 8,
                  }}
                >
                  Scanners ({tally.scanners?.length ?? 0})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(tally.scanners ?? []).map((u) => (
                    <Tooltip key={u._id} title={u.name}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          background: "#f1f5f9",
                          borderRadius: 20,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#334155",
                          cursor: "default",
                        }}
                      >
                        <UserAvatar name={u.name} size={20} />
                        {u.name}
                      </div>
                    </Tooltip>
                  ))}
                  {(!tally.scanners || tally.scanners.length === 0) && (
                    <span style={{ color: "#94a3b8", fontSize: 13 }}>
                      No scans yet
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            <StatBox
              label="Total Scanned"
              value={tally.totalScanned ?? 0}
              color="#d97706"
              bg="#fef3c7"
              icon={CheckCircleOutline}
            />
            <StatBox
              label="Tallied"
              value={tally.talliedCount ?? 0}
              color={theme.palette.success.main}
              bg={alpha(theme.palette.success.main, 0.12)}
              icon={CheckCircleOutline}
            />
            <StatBox
              label="Missing Physical"
              value={tally.missingPhysicalCount ?? 0}
              color={theme.palette.error.main}
              bg={alpha(theme.palette.error.main, 0.12)}
              icon={WarningAmberOutlined}
            />
            <StatBox
              label="Missing System"
              value={tally.missingSystemCount ?? 0}
              color={theme.palette.secondary.main}
              bg={alpha(theme.palette.secondary.main, 0.12)}
              icon={HelpOutline}
            />
          </div>
          <div
            style={{
              background: theme.palette.background.paper,
              borderRadius: 14,
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                borderBottom: `1px solid ${theme.palette.divider}`,
                background: alpha(theme.palette.text.primary, 0.04),
              }}
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                const count = tally?.[tab.countKey] ?? 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 7,
                      padding: "13px 16px",
                      border: "none",
                      borderBottom: isActive
                        ? `3px solid ${tab.color}`
                        : "3px solid transparent",
                      background: isActive ? "#fff" : "transparent",
                      cursor: "pointer",
                      fontWeight: isActive ? 800 : 600,
                      fontSize: 13,
                      color: isActive ? tab.color : "#64748b",
                      transition: "all 0.15s",
                      fontFamily: "inherit",
                    }}
                  >
                    <span
                      style={{
                        color: isActive ? tab.color : "#94a3b8",
                        display: "flex",
                      }}
                    >
                      {tab.icon}
                    </span>
                    {tab.label}
                    <span
                      style={{
                        background: isActive ? tab.color : "#f1f5f9",
                        color: isActive ? "#fff" : "#64748b",
                        borderRadius: 12,
                        padding: "1px 8px",
                        fontSize: 11,
                        fontWeight: 800,
                        fontFamily: "monospace",
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            {activeTab === "tallied" && (
              <GroupedTalliedContent rows={tally.talliedOrders} />
            )}
            {activeTab === "missingPhysical" && (
              <GroupedMissingPhysicalContent rows={tally.missingPhysical} />
            )}
            {activeTab === "missingSystem" && (
              <GroupedMissingSystemContent rows={tally.missingSystem} />
            )}
          </div>
        </>
      )}
    </BreadcrumbLayout>
  );
};

export default BranchTallyDetailScreen;
